import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, Envelope, X, EyeSlash, Eye, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  listSurveys,
  createSurvey,
  deleteSurvey,
  listSurveySubmissions,
} from "../lib/api";

const Q_TYPES = [
  { id: "multiple_choice", label: "Multiple choice (radio)" },
  { id: "checkboxes", label: "Checkboxes (multi)" },
  { id: "yes_no", label: "Yes / No" },
  { id: "short_text", label: "Short text" },
];

function QuestionEditor({ q, onChange, onRemove, idx }) {
  const needsOptions = q.type === "multiple_choice" || q.type === "checkboxes";
  const opts = needsOptions ? q.options : q.type === "yes_no" ? ["Yes", "No"] : [];

  return (
    <div className="bg-[#121212] border border-white/10 p-4 space-y-3" data-testid={`question-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="font-mono text-[10px] text-zinc-500 mt-2">Q{idx + 1}</div>
        <div className="flex-1 space-y-2">
          <Input
            data-testid={`question-label-${idx}`}
            value={q.label}
            onChange={(e) => onChange({ ...q, label: e.target.value })}
            placeholder="Question text (e.g. Do you own a home?)"
            className="rounded-none bg-[#0a0a0a] border-white/10 h-10"
          />
          <div className="flex gap-2">
            <Select value={q.type} onValueChange={(v) => onChange({ ...q, type: v, options: v === "yes_no" ? ["Yes", "No"] : q.options, points: {} })}>
              <SelectTrigger className="rounded-none bg-[#0a0a0a] border-white/10 h-9 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                {Q_TYPES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 px-3 border border-white/10 bg-[#0a0a0a] text-xs text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={q.required}
                onChange={(e) => onChange({ ...q, required: e.target.checked })}
              />
              required
            </label>
          </div>
          {needsOptions && (
            <div className="space-y-1.5">
              {q.options.map((o, i) => (
                <div key={`opt-${i}`} className="flex gap-2 items-center">
                  <Input
                    value={o}
                    onChange={(e) => {
                      const next = [...q.options];
                      next[i] = e.target.value;
                      onChange({ ...q, options: next });
                    }}
                    placeholder={`Option ${i + 1}`}
                    className="rounded-none bg-[#0a0a0a] border-white/10 h-9 text-sm flex-1"
                  />
                  <Input
                    type="number"
                    value={q.points[o] ?? 0}
                    onChange={(e) => {
                      const next = { ...q.points, [o]: parseInt(e.target.value || "0", 10) };
                      onChange({ ...q, points: next });
                    }}
                    className="rounded-none bg-[#0a0a0a] border-white/10 h-9 text-sm w-20 font-mono"
                    title="Points for this answer"
                  />
                  <button
                    onClick={() => onChange({ ...q, options: q.options.filter((_, k) => k !== i) })}
                    className="p-1.5 border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => onChange({ ...q, options: [...q.options, ""] })}
                className="text-xs font-mono text-[#ccff00] hover:underline"
              >
                + add option
              </button>
            </div>
          )}
          {q.type === "yes_no" && (
            <div className="flex gap-2">
              {opts.map((o) => (
                <div key={o} className="flex items-center gap-2 border border-white/10 px-3 py-1.5 bg-[#0a0a0a]">
                  <span className="text-sm">{o}</span>
                  <Input
                    type="number"
                    value={q.points[o] ?? 0}
                    onChange={(e) => {
                      const next = { ...q.points, [o]: parseInt(e.target.value || "0", 10) };
                      onChange({ ...q, points: next });
                    }}
                    className="rounded-none bg-[#050505] border-white/10 h-7 text-xs w-16 font-mono"
                  />
                  <span className="font-mono text-[10px] text-zinc-500">pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onRemove}
          data-testid={`remove-question-${idx}`}
          className="p-2 border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400"
        >
          <Trash size={14} />
        </button>
      </div>
    </div>
  );
}

function newQuestion() {
  return { type: "yes_no", label: "", options: ["Yes", "No"], points: { Yes: 1, No: 0 }, required: true };
}

export default function Surveys() {
  const [surveys, setSurveys] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [senderName, setSenderName] = useState("");
  const [destEmail, setDestEmail] = useState("");
  const [notifyWebhookUrl, setNotifyWebhookUrl] = useState("");
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplySubject, setAutoReplySubject] = useState("Thanks for your interest — we'll be in touch");
  const [autoReplyHtml, setAutoReplyHtml] = useState(
    "<p>Hi {name},</p>\n<p>Thanks for filling out the form. A team member will reach out within 24 hours.</p>\n<p>Cheers,<br/>{sender_name}</p>",
  );
  const [minScore, setMinScore] = useState(1);
  const [questions, setQuestions] = useState([newQuestion()]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [submissions, setSubmissions] = useState({});

  const refresh = useCallback(async () => {
    try {
      setSurveys(await listSurveys());
    } catch (err) {
      toast.error("Failed to load surveys");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const expand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    try {
      const subs = await listSurveySubmissions(id);
      setSubmissions((s) => ({ ...s, [id]: subs }));
    } catch (err) {
      toast.error("Failed to load submissions");
    }
  };

  const doCreate = async () => {
    if (!name.trim() || !senderName.trim() || !destEmail.trim())
      return toast.error("Name, origin tag, and destination email are required");
    if (questions.some((q) => !q.label.trim()))
      return toast.error("All questions must have a label");
    setLoading(true);
    try {
      await createSurvey({
        name: name.trim(),
        sender_name: senderName.trim(),
        destination_email: destEmail.trim(),
        questions,
        qualifier: { min_score: minScore, required_answers: [] },
        collect_email: true,
        collect_name: true,
        collect_phone: false,
        notify_webhook_url: notifyWebhookUrl.trim() || null,
        auto_reply_enabled: autoReplyEnabled,
        auto_reply_subject: autoReplySubject.trim(),
        auto_reply_html: autoReplyHtml,
      });
      toast.success("Survey created");
      setName("");
      setSenderName("");
      setDestEmail("");
      setNotifyWebhookUrl("");
      setAutoReplyEnabled(false);
      setMinScore(1);
      setQuestions([newQuestion()]);
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this survey?")) return;
    await deleteSurvey(id);
    refresh();
    toast.success("Survey deleted");
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-3">lead capture</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
            Qualifier <span className="font-black text-[#ccff00]">Surveys</span>
          </h1>
          <p className="text-zinc-400 mt-3 max-w-lg leading-relaxed">
            Attach a mini survey between the ad click and the affiliate URL.
            Qualifying leads trigger an emailed lead slip tagged with your
            origin — non-qualifying visitors still get through.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-survey-btn"
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-semibold px-6 py-3 flex items-center gap-2 transition-colors"
            >
              <Plus size={18} weight="bold" />
              New survey
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                Create qualifier survey
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-sm">
                Configure questions + a min-score threshold. Qualifying leads
                trigger a lead-slip email to your destination address.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-2">survey name</div>
                  <Input
                    data-testid="survey-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Home Solar Lead"
                    className="rounded-none bg-[#121212] border-white/10 h-11"
                  />
                </div>
                <div>
                  <div className="label-mono mb-2">origin tag (sender_name)</div>
                  <Input
                    data-testid="sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="e.g. john-affiliate"
                    className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                  />
                </div>
                <div>
                  <div className="label-mono mb-2">destination email</div>
                  <Input
                    data-testid="dest-email"
                    type="email"
                    value={destEmail}
                    onChange={(e) => setDestEmail(e.target.value)}
                    placeholder="leads@yourdomain.com"
                    className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                  />
                </div>
                <div>
                  <div className="label-mono mb-2">min score to qualify</div>
                  <Input
                    data-testid="min-score"
                    type="number"
                    value={minScore}
                    onChange={(e) => setMinScore(parseInt(e.target.value || "0", 10))}
                    className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <div className="label-mono mb-2">forward qualified leads to webhook (optional)</div>
                <Input
                  data-testid="notify-webhook-url"
                  type="url"
                  value={notifyWebhookUrl}
                  onChange={(e) => setNotifyWebhookUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/hooks/catch/… or your CRM endpoint"
                  className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                />
                <div className="text-[10px] text-zinc-500 font-mono mt-1">
                  fires only on qualified submissions · POST JSON payload · contact + geo + answers + ad context
                </div>
              </div>

              {/* Auto-reply to leads */}
              <div className="border border-white/10 bg-[#0a0a0a] p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="auto-reply-enabled"
                    checked={autoReplyEnabled}
                    onChange={(e) => setAutoReplyEnabled(e.target.checked)}
                    className="mt-1 accent-[#ccff00]"
                  />
                  <div className="flex-1">
                    <div className="text-sm text-zinc-200 font-medium">Send instant auto-reply to the lead</div>
                    <div className="text-[11px] text-zinc-500 font-mono mt-1">
                      fires the moment the visitor submits · use <code className="text-[#ccff00]">{"{name}"}</code> and <code className="text-[#ccff00]">{"{sender_name}"}</code> as placeholders
                    </div>
                  </div>
                </label>
                {autoReplyEnabled && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <div className="label-mono mb-2">subject</div>
                      <Input
                        data-testid="auto-reply-subject"
                        value={autoReplySubject}
                        onChange={(e) => setAutoReplySubject(e.target.value)}
                        className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <div className="label-mono mb-2">html body</div>
                      <textarea
                        data-testid="auto-reply-html"
                        value={autoReplyHtml}
                        onChange={(e) => setAutoReplyHtml(e.target.value)}
                        rows={6}
                        className="w-full rounded-none bg-[#121212] border border-white/10 p-3 font-mono text-xs text-zinc-200 focus:border-[#ccff00]/40 focus:outline-none resize-y"
                      />
                      <div className="text-[10px] text-zinc-500 font-mono mt-1">
                        HTML allowed · plain-text fallback auto-generated · reply-to routes back to your destination email
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="label-mono">questions</div>
                  <button
                    data-testid="add-question-btn"
                    onClick={() => setQuestions([...questions, newQuestion()])}
                    className="font-mono text-[10px] uppercase tracking-wider text-[#ccff00] hover:underline"
                  >
                    + add question
                  </button>
                </div>
                <div className="space-y-2">
                  {questions.map((q, i) => (
                    <QuestionEditor
                      key={`q-${i}`}
                      q={q}
                      idx={i}
                      onChange={(nq) => {
                        const next = [...questions];
                        next[i] = nq;
                        setQuestions(next);
                      }}
                      onRemove={() => setQuestions(questions.filter((_, k) => k !== i))}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button
                data-testid="save-survey-btn"
                onClick={doCreate}
                disabled={loading}
                className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 px-6 py-2.5 font-semibold w-full"
              >
                {loading ? "Saving…" : "Save survey"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="surveys-list">
        {surveys.length === 0 && (
          <div className="bg-[#0a0a0a] border border-white/10 px-6 py-16 text-center text-zinc-500">
            <Envelope size={40} className="mx-auto mb-3 opacity-40" />
            <div className="font-mono text-sm">no surveys yet</div>
          </div>
        )}
        {surveys.map((s) => {
          const isOpen = expandedId === s.id;
          const subs = submissions[s.id] || [];
          return (
            <div
              key={s.id}
              data-testid={`survey-${s.id}`}
              className="bg-[#0a0a0a] border border-white/10 hover:border-white/20 transition-colors"
            >
              <div className="px-6 py-4 flex items-center justify-between gap-4">
                <button onClick={() => expand(s.id)} className="flex-1 text-left min-w-0">
                  <div className="font-display font-bold truncate">{s.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">
                    origin: {s.sender_name} → {s.destination_email} · {s.questions.length} question(s)
                  </div>
                </button>
                <div className="flex gap-4 items-center flex-shrink-0">
                  <div className="hidden md:flex gap-4 text-center">
                    <div>
                      <div className="font-mono text-lg font-bold text-[#ccff00]">
                        {s.qualified_count}
                      </div>
                      <div className="label-mono text-[9px]">qualified</div>
                    </div>
                    <div>
                      <div className="font-mono text-lg font-bold text-zinc-300">
                        {s.submissions_count}
                      </div>
                      <div className="label-mono text-[9px]">total</div>
                    </div>
                  </div>
                  <button
                    onClick={() => expand(s.id)}
                    className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00]"
                    title={isOpen ? "Hide" : "View submissions"}
                  >
                    {isOpen ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    data-testid={`delete-survey-${s.id}`}
                    className="p-2 bg-[#121212] border border-white/10 hover:border-red-500 hover:text-red-500"
                    title="Delete"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="px-6 pb-5" data-testid={`survey-submissions-${s.id}`}>
                  {subs.length === 0 ? (
                    <div className="text-xs text-zinc-500 font-mono">no submissions yet</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left label-mono">
                          <th className="py-2">time</th>
                          <th>contact</th>
                          <th>score</th>
                          <th>status</th>
                          <th>ad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subs.map((r) => (
                          <tr key={r.id} className="border-t border-white/5">
                            <td className="py-2 font-mono text-zinc-500">{r.submitted_at.slice(0, 16).replace("T", " ")}</td>
                            <td className="text-zinc-200">
                              {r.contact_name || "—"} · {r.contact_email || "—"}
                            </td>
                            <td className="font-mono">{r.score}</td>
                            <td>
                              <span
                                className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${
                                  r.qualified
                                    ? "text-[#ccff00] border-[#ccff00]/40 bg-[#ccff00]/5"
                                    : "text-zinc-400 border-white/10"
                                }`}
                              >
                                {r.qualified ? "qualified" : "not"}
                              </span>
                            </td>
                            <td className="font-mono text-zinc-500">
                              {r.platform} · {r.variant_id?.slice(0, 6)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
